import { useCallback, useEffect, useMemo, useState } from "react";
import { listarPromociones } from "../../../../services/promocionesService";
import { normalizePromocion } from "../adapters/promocionesAdapter";
import { showToast } from "../../../../utils/showToast";

export default function usePromocionesList() {
  const [promociones, setPromociones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filtro, setFiltro] = useState({ tipo: "all", valor: null });
  const [filtros, setFiltros] = useState({
    search: "",
    estado: "all",
    activo: "all",
    curso_id: "all",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchPromociones = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarPromociones();
      const normalized = (Array.isArray(data) ? data : []).map(normalizePromocion);
      setPromociones(normalized);
      setListError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
      const message = err.message || "No se pudo obtener la lista de promociones.";
      setListError(message);
      showToast({ type: "danger", message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPromociones();
  }, [fetchPromociones]);

  const resumen = useMemo(() => {
    const total = promociones.length;
    const activas = promociones.filter((promo) => promo.activo).length;
    const inactivas = total - activas;
    const programadas = promociones.filter(
      (promo) => (promo.estado || "").toLowerCase() === "programado"
    ).length;
    const enCurso = promociones.filter(
      (promo) => (promo.estado || "").toLowerCase() === "en_curso"
    ).length;
    const finalizadas = promociones.filter(
      (promo) => (promo.estado || "").toLowerCase() === "finalizado"
    ).length;
    const centros = new Set(
      promociones
        .map((promo) => (promo.centroNombre || "").toLowerCase())
        .filter(Boolean)
    ).size;
    return { total, activas, inactivas, programadas, enCurso, finalizadas, centros };
  }, [promociones]);

  const promocionesFiltradas = useMemo(() => {
    let result = promociones.filter((promo) => {
      if (filtros.activo === "1" && !promo.activo) return false;
      if (filtros.activo === "0" && promo.activo) return false;
      if (filtros.estado !== "all" && filtros.estado) {
        if ((promo.estado || "").toLowerCase() !== String(filtros.estado || "").toLowerCase()) return false;
      }
      if (filtros.curso_id !== "all" && filtros.curso_id) {
        if (String(promo.cursoId || "") !== String(filtros.curso_id)) return false;
      }
      if (filtros.search) {
        const term = filtros.search.toLowerCase();
        const hay =
          (promo.nombre || "").toLowerCase().includes(term) ||
          (promo.cursoNombre || "").toLowerCase().includes(term) ||
          (promo.centroNombre || "").toLowerCase().includes(term) ||
          (promo.cicloCodigo || "").toLowerCase().includes(term) ||
          (promo.cicloNombre || "").toLowerCase().includes(term);
        if (!hay) return false;
      }
      return true;
    });

    if (filtro.tipo === "activo") {
      result = result.filter((promo) => Boolean(promo.activo) === Boolean(filtro.valor));
    }
    if (filtro.tipo === "estado") {
      result = result.filter(
        (promo) => (promo.estado || "").toLowerCase() === String(filtro.valor || "").toLowerCase()
      );
    }
    return result;
  }, [promociones, filtros, filtro]);

  const total = promocionesFiltradas.length;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [filtros, filtro, total]);

  const promocionesPaginadas = useMemo(() => {
    const start = (page - 1) * pageSize;
    return promocionesFiltradas.slice(start, start + pageSize);
  }, [promocionesFiltradas, page, pageSize]);

  const goToPage = useCallback(
    (nextPage) => {
      const safe = Math.min(totalPages, Math.max(1, nextPage));
      setPage(safe);
    },
    [totalPages]
  );

  const aplicarFiltro = useCallback((tipo, valor = null) => {
    setFiltro((prev) => {
      const same = prev.tipo === tipo && prev.valor === valor;
      return same ? { tipo: "all", valor: null } : { tipo, valor };
    });
  }, []);

  const upsertPromocion = useCallback((promocion) => {
    setPromociones((prev) => {
      const exists = prev.some((p) => String(p.id) === String(promocion.id));
      return exists
        ? prev.map((p) => (String(p.id) === String(promocion.id) ? promocion : p))
        : [promocion, ...prev];
    });
    setLastUpdated(new Date());
  }, []);

  const removePromocion = useCallback((id) => {
    setPromociones((prev) => prev.filter((p) => String(p.id) !== String(id)));
    setLastUpdated(new Date());
  }, []);

  const limpiarFiltros = useCallback(
    () =>
      setFiltros({
        search: "",
        estado: "all",
        activo: "all",
        curso_id: "all",
      }),
    []
  );

  return {
    promociones,
    promocionesFiltradas,
    promocionesPaginadas,
    resumen,
    filtro,
    filtros,
    setFiltros,
    limpiarFiltros,
    aplicarFiltro,
    loading,
    listError,
    lastUpdated,
    page,
    pageSize,
    setPageSize,
    totalPages,
    total,
    goToPage,
    reload: fetchPromociones,
    setPromociones,
    upsertPromocion,
    removePromocion,
  };
}
